/**
 * Contacts main menu – list contacts with pagination, search, selection, and row actions (call, email, SuperFaktura). Default sort: createdAt desc.
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiUrl, apiFetch } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { useDebounce } from "@/hooks/useDebounce";
import { useAuthLite } from "@/context/AuthLite";
import type { Contact } from "@shared/api";
import type { ContactsListPaginatedResponse } from "@shared/api";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { bottomSheetDockClassName } from "@/lib/bottom-sheet-compact";
import {
  Phone,
  Mail,
  ExternalLink,
  Loader2,
  UserPlus,
  ChevronLeft,
  ChevronRight,
  CheckSquare,
  Download,
  Trash2,
  X,
} from "lucide-react";
import { notify } from "@/lib/notify";
import { cn } from "@/lib/utils";
import { selectionItemOutlineClasses } from "@/lib/selection-item-styles";
import { AppEmptyState } from "@/components/ui/app-empty-state";
import { FabMenu } from "@/components/ui/fab-menu";
import { useIsMobile } from "@/hooks/use-mobile";
import { ContactsHubShell } from "@/components/contacts/ContactsHubShell";
import { SelectionLiveRegion } from "@/components/ui/selection-live-region";
import { SelectionToolbarLayout } from "@/components/ui/selection-toolbar-layout";
import { useSelectionKeyboardShortcuts } from "@/hooks/use-selection-keyboard-shortcuts";
import { hapticTap } from "@/lib/haptics";
import { useLongPress } from "@/hooks/use-long-press";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { MoreHorizontal } from "lucide-react";
import { ContactsCallingFilterPanel } from "@/components/contacts/ContactsCallingFilterPanel";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  buildUserScopedKey,
  loadSortPrefV1,
  migrateAnonToUserScopedV1,
  saveSortPrefV1,
} from "@/lib/user-scoped-storage";
const PAGE_SIZE_OPTIONS = [50, 100, 200];

type ContactsSortBy = "name" | "email" | "createdAt" | "city" | "closingDate";
type ContactsSortOrder = "asc" | "desc";
const CONTACTS_SORT_FEATURE_KEY = "contacts.sort";
const CONTACTS_SORT_BY_ALLOWED = ["name", "email", "createdAt", "city", "closingDate"] as const;
const CONTACTS_SORT_ORDER_ALLOWED = ["asc", "desc"] as const;

function exportContactsToCsv(contacts: Contact[], selectedIds: Set<string>): void {
  const toExport = selectedIds.size > 0
    ? contacts.filter((c) => selectedIds.has(c.id))
    : contacts;
  if (toExport.length === 0) {
    return;
  }
  const headers = ["name", "email", "phone", "city", "street"];
  const rows = toExport.map((c) =>
    headers.map((h) => {
      const raw = c as unknown as Record<string, unknown>;
      const v = raw[h] ?? (h === "street" ? raw.address : undefined);
      const s = String(v ?? "");
      return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(",")
  );
  const csv = [headers.join(","), ...rows].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const d = new Date();
  const localDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  a.download = `contacts-${localDate}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function ContactsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const { user } = useAuthLite();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [sortBy, setSortBy] = useState<ContactsSortBy>("createdAt");
  const [sortOrder, setSortOrder] = useState<ContactsSortOrder>("desc");
  const [searchInput, setSearchInput] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterEmail, setFilterEmail] = useState("");
  const [filterCreatedAfter, setFilterCreatedAfter] = useState("");
  const [filterCreatedBefore, setFilterCreatedBefore] = useState("");
  const [selectionModeEnabled, setSelectionModeEnabled] = useState(false);
  const [selectedContactIds, setSelectedContactIds] = useState<Set<string>>(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [acName, setAcName] = useState("");
  const [acEmail, setAcEmail] = useState("");
  const [acPhone, setAcPhone] = useState("");
  const [acCity, setAcCity] = useState("");
  const [acStreet, setAcStreet] = useState("");
  const [acIsCompany, setAcIsCompany] = useState(false);
  const [acIco, setAcIco] = useState("");
  const debouncedSearch = useDebounce(searchInput, 300);
  const debouncedFilterName = useDebounce(filterName, 300);
  const debouncedFilterEmail = useDebounce(filterEmail, 300);

  // Restore persisted sort settings (user-scoped; shared across desktop+mobile)
  useEffect(() => {
    migrateAnonToUserScopedV1({ userEmail: user?.email, featureKey: CONTACTS_SORT_FEATURE_KEY });
    const scopedKey = buildUserScopedKey(user?.email, CONTACTS_SORT_FEATURE_KEY);
    const pref = loadSortPrefV1<ContactsSortBy, ContactsSortOrder>(
      scopedKey,
      CONTACTS_SORT_BY_ALLOWED,
      CONTACTS_SORT_ORDER_ALLOWED,
      { sortBy: "createdAt", sortOrder: "desc" },
    );
    setSortBy(pref.sortBy);
    setSortOrder(pref.sortOrder);
  }, [user?.email]);

  // Persist sort settings
  useEffect(() => {
    const scopedKey = buildUserScopedKey(user?.email, CONTACTS_SORT_FEATURE_KEY);
    saveSortPrefV1<ContactsSortBy, ContactsSortOrder>(scopedKey, { sortBy, sortOrder });
  }, [sortBy, sortOrder, user?.email]);

  const query = useQuery({
    queryKey: [
      ...queryKeys.crmContacts,
      "paginated",
      page,
      pageSize,
      sortBy,
      sortOrder,
      debouncedSearch,
      debouncedFilterName,
      debouncedFilterEmail,
      filterCreatedAfter,
      filterCreatedBefore,
    ],
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("pageSize", String(pageSize));
      if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());
      if (debouncedFilterName.trim()) params.set("filterName", debouncedFilterName.trim());
      if (debouncedFilterEmail.trim()) params.set("filterEmail", debouncedFilterEmail.trim());
      if (filterCreatedAfter.trim()) params.set("createdAfter", filterCreatedAfter.trim());
      if (filterCreatedBefore.trim()) params.set("createdBefore", filterCreatedBefore.trim());
      params.set("sortBy", sortBy);
      params.set("sortOrder", sortOrder);
      const r = await apiFetch(`/api/crm/contacts?${params.toString()}`, { credentials: "include", signal });
      if (!r.ok) throw new Error("Contacts betöltése sikertelen");
      return (await r.json()) as ContactsListPaginatedResponse;
    },
  });

  const { contacts = [], total = 0 } = query.data ?? {};
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const selectionMode = selectionModeEnabled;
  const bulkDismissRef = useRef<HTMLButtonElement>(null);
  const bulkDismissDesktopRef = useRef<HTMLButtonElement>(null);

  const exitSelectionMode = useCallback(() => {
    hapticTap(12);
    setSelectionModeEnabled(false);
    setSelectedContactIds(new Set());
  }, []);

  useSelectionKeyboardShortcuts({
    enabled: selectionMode,
    onExit: exitSelectionMode,
    onSelectAllInView: () => {
      if (contacts.length === 0) return;
      setSelectedContactIds(new Set(contacts.map((c) => c.id)));
    },
  });

  useEffect(() => {
    if (!selectionMode) return;
    const target = isMobile ? bulkDismissRef.current : bulkDismissDesktopRef.current;
    const id = requestAnimationFrame(() => target?.focus());
    return () => cancelAnimationFrame(id);
  }, [selectionMode, isMobile]);

  const handleToggleSelect = useCallback((contactId: string, checked: boolean) => {
    setSelectedContactIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(contactId);
      else next.delete(contactId);
      return next;
    });
  }, []);

  const handleExportCsv = useCallback(() => {
    if (selectedContactIds.size === 0) {
      notify.info(t("contacts.clearSelection"));
      return;
    }
    exportContactsToCsv(contacts, selectedContactIds);
    notify.success(t("contacts.export"));
  }, [contacts, selectedContactIds, t]);

  const resetAddContactForm = useCallback(() => {
    setAcName("");
    setAcEmail("");
    setAcPhone("");
    setAcCity("");
    setAcStreet("");
    setAcIsCompany(false);
    setAcIco("");
  }, []);

  const createContactMutation = useMutation({
    mutationFn: async (payload: {
      name: string;
      email?: string;
      phone?: string;
      city?: string;
      street?: string;
      isCompany: boolean;
      ico?: string;
    }) => {
      const r = await apiFetch("/api/crm/contacts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      const j = (await r.json().catch(() => ({}))) as Contact & {
        existing?: boolean;
        message?: string;
        userMessage?: string;
        details?: { message?: string };
      };
      if (!r.ok) {
        const msg =
          j.userMessage ||
          j.details?.message ||
          j.message ||
          t("contacts.addContactCreateError");
        throw new Error(typeof msg === "string" ? msg : t("contacts.addContactCreateError"));
      }
      return j;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: [...queryKeys.crmContacts] });
      void queryClient.invalidateQueries({ queryKey: [...queryKeys.crmBoard] });
      setAddContactOpen(false);
      resetAddContactForm();
      if (data.existing) {
        notify.info(t("contacts.addContactExistingReused"));
      } else {
        notify.success(t("contacts.addContactSuccess"));
      }
      if (data.id) {
        navigate(`/contacts/${encodeURIComponent(data.id)}`);
      }
    },
    onError: (e: Error) => {
      notify.error(e.message || t("contacts.addContactCreateError"));
    },
  });

  const deleteContactsMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const deleted: string[] = [];
      let blocked = 0;
      let otherFail = 0;
      for (const id of ids) {
        const r = await apiFetch(`/api/crm/contacts/${encodeURIComponent(id)}`, { method: "DELETE" });
        if (r.ok) {
          deleted.push(id);
          continue;
        }
        const j = (await r.json().catch(() => ({}))) as { error?: string };
        if (r.status === 409 || j.error === "contact_linked_to_deals") blocked += 1;
        else otherFail += 1;
      }
      return { deleted, blocked, otherFail };
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [...queryKeys.crmContacts] });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.crmBoard] });
      setSelectedContactIds((prev) => {
        const next = new Set(prev);
        res.deleted.forEach((id) => next.delete(id));
        return next;
      });
      setDeleteDialogOpen(false);
      const { deleted, blocked, otherFail } = res;
      if (otherFail > 0) {
        notify.error(
          t("contacts.deletePartialError", { ok: deleted.length, fail: blocked + otherFail }),
        );
      } else if (blocked > 0 && deleted.length === 0) {
        notify.info(t("contacts.deleteBlockedByDealsBulk", { count: blocked }));
      } else if (blocked > 0) {
        notify.success(t("contacts.deletePartialBlocked", { ok: deleted.length, blocked }));
      } else if (deleted.length > 0) {
        notify.success(t("contacts.deleteSuccess", { count: deleted.length }));
      }
    },
    onError: () => {
      notify.error(t("contacts.deleteError"));
    },
  });

  const handleToggleSelectionMode = () => {
    setSelectionModeEnabled((prev) => {
      if (prev) setSelectedContactIds(new Set());
      return !prev;
    });
  };

  const clearAllFilters = () => {
    setSearchInput("");
    setFilterName("");
    setFilterEmail("");
    setFilterCreatedAfter("");
    setFilterCreatedBefore("");
    setPage(1);
  };

  const hasActiveListFilters =
    debouncedSearch.trim() !== "" ||
    debouncedFilterName.trim() !== "" ||
    debouncedFilterEmail.trim() !== "" ||
    filterCreatedAfter.trim() !== "" ||
    filterCreatedBefore.trim() !== "";

  const sidebar = (
    <>
      <div className="space-y-4 rounded-md border bg-card p-4">
        <ContactsCallingFilterPanel
          mode="contacts"
          searchPlaceholder={t("contacts.searchPlaceholder")}
          searchValue={searchInput}
          onSearchChange={(v) => {
            setSearchInput(v);
            setPage(1);
          }}
          filterName={filterName}
          filterEmail={filterEmail}
          filterCreatedAfter={filterCreatedAfter}
          filterCreatedBefore={filterCreatedBefore}
          onFilterNameChange={(v) => {
            setFilterName(v);
            setPage(1);
          }}
          onFilterEmailChange={(v) => {
            setFilterEmail(v);
            setPage(1);
          }}
          onFilterCreatedAfterChange={(v) => {
            setFilterCreatedAfter(v);
            setPage(1);
          }}
          onFilterCreatedBeforeChange={(v) => {
            setFilterCreatedBefore(v);
            setPage(1);
          }}
        />
      </div>
      <div className="mb-0 mt-3 space-y-2 rounded-md border bg-card p-3 md:hidden">
        <span className="text-xs font-medium text-muted-foreground">{t("contacts.sortBy")}</span>
        <Select
          value={sortBy}
          onValueChange={(v) => {
            setSortBy(v as ContactsSortBy);
            setPage(1);
          }}
        >
          <SelectTrigger className="h-9 w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="name">{t("contacts.sortByName")}</SelectItem>
            <SelectItem value="email">{t("contacts.sortByEmail")}</SelectItem>
            <SelectItem value="city">{t("contacts.sortByCity")}</SelectItem>
            <SelectItem value="createdAt">{t("contacts.sortByCreatedAt")}</SelectItem>
            <SelectItem value="closingDate">{t("contacts.sortByClosingDate")}</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={sortOrder}
          onValueChange={(v) => {
            setSortOrder(v as ContactsSortOrder);
            setPage(1);
          }}
        >
          <SelectTrigger className="h-9 w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="asc">{t("crm.sortOrderAsc")}</SelectItem>
            <SelectItem value="desc">{t("crm.sortOrderDesc")}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Button variant="outline" className="w-full" type="button" onClick={clearAllFilters}>
        {t("contacts.clearFilters")}
      </Button>
    </>
  );

  const addContactFields = (
    <div className="grid gap-3 py-2">
      <div className="space-y-2">
        <Label htmlFor="ac-name">{t("contacts.contactName")}</Label>
        <Input
          id="ac-name"
          value={acName}
          onChange={(e) => setAcName(e.target.value)}
          autoComplete="name"
          placeholder={t("contacts.contactName")}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="ac-email">{t("contacts.email")}</Label>
        <Input
          id="ac-email"
          type="email"
          value={acEmail}
          onChange={(e) => setAcEmail(e.target.value)}
          autoComplete="email"
          placeholder={t("contacts.email")}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="ac-phone">{t("contacts.mobile")}</Label>
        <Input
          id="ac-phone"
          type="tel"
          value={acPhone}
          onChange={(e) => setAcPhone(e.target.value)}
          autoComplete="tel"
          placeholder={t("contacts.mobile")}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="ac-city">{t("contacts.mailingCity")}</Label>
        <Input
          id="ac-city"
          value={acCity}
          onChange={(e) => setAcCity(e.target.value)}
          autoComplete="address-level2"
          placeholder={t("contacts.mailingCity")}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="ac-street">{t("contacts.mailingStreet")}</Label>
        <Input
          id="ac-street"
          value={acStreet}
          onChange={(e) => setAcStreet(e.target.value)}
          autoComplete="street-address"
          placeholder={t("contacts.mailingStreet")}
        />
      </div>
      <div className="flex items-center gap-2">
        <Checkbox
          id="ac-company"
          checked={acIsCompany}
          onCheckedChange={(v) => setAcIsCompany(v === true)}
        />
        <Label htmlFor="ac-company" className="text-sm font-normal leading-none">
          {t("contacts.addContactCompanyToggle")}
        </Label>
      </div>
      {acIsCompany ? (
        <div className="space-y-2">
          <Label htmlFor="ac-ico">{t("contacts.addContactIco")}</Label>
          <Input id="ac-ico" value={acIco} onChange={(e) => setAcIco(e.target.value)} placeholder={t("contacts.addContactIco")} />
        </div>
      ) : null}
    </div>
  );

  const closeAddContact = () => {
    resetAddContactForm();
    setAddContactOpen(false);
  };

  return (
    <ContactsHubShell
      title={t("contacts.allContacts")}
      activeTab="contacts"
      sidebar={sidebar}
      selectionModeActive={selectionMode}
      selectionToolbar={
        <SelectionToolbarLayout
          closeButtonRef={bulkDismissDesktopRef}
          className="px-0 sm:px-0"
          selectedCount={selectedContactIds.size}
          onClose={exitSelectionMode}
          onSelectAll={() => setSelectedContactIds(new Set(contacts.map((c) => c.id)))}
          selectAllDisabled={contacts.length === 0}
          rightActions={
            <Button
              variant="outline"
              size="sm"
              className="shrink-0 gap-1"
              onClick={handleExportCsv}
              disabled={selectedContactIds.size === 0}
            >
              <Download className="h-4 w-4" />
              {t("contacts.export")}
            </Button>
          }
          onDelete={() => setDeleteDialogOpen(true)}
          deleteDisabled={selectedContactIds.size === 0}
          deletePending={deleteContactsMutation.isPending}
        />
      }
      headerRight={
        <div className="flex flex-wrap items-center justify-end gap-2">
          {isMobile ? (
            <Button
              variant="outline"
              size="sm"
              className="h-9 w-9 shrink-0 p-0"
              type="button"
              disabled={contacts.length === 0}
              onClick={() => {
                hapticTap(12);
                if (selectionModeEnabled) exitSelectionMode();
                else setSelectionModeEnabled(true);
              }}
              aria-label={selectionModeEnabled ? t("contacts.selectionOff") : t("contacts.selection")}
            >
              {selectionModeEnabled ? <X className="h-4 w-4" /> : <CheckSquare className="h-4 w-4" />}
            </Button>
          ) : null}
          {!isMobile && !selectionModeEnabled ? (
            <Button variant="outline" size="sm" className="shrink-0 gap-1" onClick={handleToggleSelectionMode}>
              {t("common.selectTrigger")}
            </Button>
          ) : null}
          <Button
            variant="default"
            size="sm"
            className="hidden shrink-0 gap-1 md:inline-flex"
            type="button"
            onClick={() => setAddContactOpen(true)}
          >
            <UserPlus className="h-4 w-4" />
            <span className="hidden sm:inline">{t("contacts.addContact")}</span>
          </Button>
        </div>
      }
    >
      <div className="min-h-0 space-y-4">
        <div className="hidden flex-wrap items-center gap-2 rounded-md border bg-card px-3 py-2 md:flex">
          <span className="text-sm text-muted-foreground shrink-0">{t("contacts.sortBy")}</span>
          <Select
            value={sortBy}
            onValueChange={(v) => {
              setSortBy(v as ContactsSortBy);
              setPage(1);
            }}
          >
            <SelectTrigger className={cn("h-9", isMobile ? "min-w-[130px] flex-1" : "w-[200px]")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name">{t("contacts.sortByName")}</SelectItem>
              <SelectItem value="email">{t("contacts.sortByEmail")}</SelectItem>
              <SelectItem value="city">{t("contacts.sortByCity")}</SelectItem>
              <SelectItem value="createdAt">{t("contacts.sortByCreatedAt")}</SelectItem>
              <SelectItem value="closingDate">{t("contacts.sortByClosingDate")}</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={sortOrder}
            onValueChange={(v) => {
              setSortOrder(v as ContactsSortOrder);
              setPage(1);
            }}
          >
            <SelectTrigger className={cn("h-9", isMobile ? "min-w-[110px] flex-1" : "w-[140px]")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="asc">{t("crm.sortOrderAsc")}</SelectItem>
              <SelectItem value="desc">{t("crm.sortOrderDesc")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {!isMobile ? (
          <div className="w-full rounded-md border overflow-x-auto min-w-0 bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  {selectionMode && (
                    <TableHead className="w-10" />
                  )}
                  <TableHead className="min-w-[180px]">{t("contacts.contactName")}</TableHead>
                  <TableHead className="min-w-[200px]">{t("contacts.email")}</TableHead>
                  <TableHead className="min-w-[140px]">{t("contacts.mobile")}</TableHead>
                  <TableHead className="min-w-[120px]">{t("contacts.mailingCity")}</TableHead>
                  <TableHead className="min-w-[160px]">{t("contacts.mailingStreet")}</TableHead>
                  <TableHead className="w-[120px] text-right">{t("contacts.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {query.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={selectionMode ? 7 : 6} className="text-center text-muted-foreground py-12">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                      {t("common.loading")}
                    </TableCell>
                  </TableRow>
                ) : query.isError ? (
                  <TableRow>
                    <TableCell colSpan={selectionMode ? 7 : 6} className="text-center text-destructive py-8">
                      {(query.error as Error)?.message ?? t("common.loadError")}
                    </TableCell>
                  </TableRow>
                ) : contacts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={selectionMode ? 7 : 6} className="py-2">
                      <AppEmptyState
                        title={hasActiveListFilters ? t("contacts.noResults") : t("contacts.noContacts")}
                        description={hasActiveListFilters ? t("search.tryDifferentQuery") : t("contacts.searchPlaceholder")}
                        secondaryAction={hasActiveListFilters ? {
                          label: t("contacts.clearSelection"),
                          onClick: clearAllFilters,
                        } : undefined}
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  contacts.map((contact: Contact) => (
                    <ContactRow
                      key={contact.id}
                      contact={contact}
                      selectionMode={selectionMode}
                      selected={selectedContactIds.has(contact.id)}
                      onToggleSelect={handleToggleSelect}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="space-y-3">
            {query.isLoading ? (
              <div className="py-12 text-center text-muted-foreground">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                {t("common.loading")}
              </div>
            ) : query.isError ? (
              <div className="py-8 text-center text-destructive">
                {(query.error as Error)?.message ?? t("common.loadError")}
              </div>
            ) : contacts.length === 0 ? (
              <AppEmptyState
                title={hasActiveListFilters ? t("contacts.noResults") : t("contacts.noContacts")}
                description={hasActiveListFilters ? t("search.tryDifferentQuery") : t("contacts.searchPlaceholder")}
                secondaryAction={hasActiveListFilters ? {
                  label: t("contacts.clearSelection"),
                  onClick: clearAllFilters,
                } : undefined}
              />
            ) : (
              contacts.map((contact) => (
                <ContactMobileCard
                  key={contact.id}
                  contact={contact}
                  selectionMode={selectionModeEnabled}
                  selected={selectedContactIds.has(contact.id)}
                  onToggleSelect={handleToggleSelect}
                  onEnterSelectionMode={(id) => {
                    hapticTap(12);
                    setSelectionModeEnabled(true);
                    setSelectedContactIds(new Set([id]));
                  }}
                />
              ))
            )}
          </div>
        )}

        {/* Pagination – same row as Hívogatás */}
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm text-muted-foreground">
            {t("contacts.totalContacts", { count: total })}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">{t("contacts.recordsPerPage")}</span>
              <Select
                value={String(pageSize)}
                onValueChange={(v) => {
                  setPageSize(Number(v));
                  setPage(1);
                }}
              >
                <SelectTrigger className={cn("h-8", isMobile ? "w-[72px]" : "w-[80px]")}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1 || query.isLoading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className={cn("text-sm text-muted-foreground text-center", isMobile ? "min-w-[84px]" : "min-w-[100px]")}>
                {from} – {to} / {total}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages || query.isLoading}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {isMobile && !selectionModeEnabled ? (
        <FabMenu
          fabAriaLabel={t("contacts.addContact")}
          actions={[
            {
              id: "add-contact",
              label: t("contacts.addContact"),
              icon: <UserPlus className="h-5 w-5" />,
              onClick: () => setAddContactOpen(true),
            },
          ]}
        />
      ) : null}

      {isMobile && selectionModeEnabled ? (
        <div className="fixed inset-x-0 z-40 px-3 pb-2" style={{ bottom: "calc(4rem + env(safe-area-inset-bottom))" }}>
          <div className="mx-auto flex max-w-xl flex-wrap items-center gap-2 rounded-xl border bg-background/95 px-3 py-2 shadow-lg backdrop-blur">
            <Button
              ref={bulkDismissRef}
              type="button"
              variant="outline"
              size="sm"
              className="h-9 w-9 shrink-0 p-0"
              aria-label={t("contacts.selectionOff")}
              onClick={exitSelectionMode}
            >
              <X className="h-4 w-4" />
            </Button>
            <SelectionLiveRegion count={selectedContactIds.size} />
            <span className="max-w-[10rem] truncate text-sm text-muted-foreground sm:max-w-none">
              {selectedContactIds.size} {t("images.selected")}
            </span>
            {selectedContactIds.size === 0 ? (
              <span className="text-sm text-muted-foreground">{t("common.selectItemsHint")}</span>
            ) : null}
            <button
              type="button"
              className="shrink-0 text-sm text-primary underline-offset-4 hover:underline disabled:pointer-events-none disabled:opacity-50 hidden sm:inline"
              disabled={contacts.length === 0}
              onClick={() => setSelectedContactIds(new Set(contacts.map((c) => c.id)))}
            >
              {t("common.selectAll")}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-9 w-9 shrink-0 p-0 ml-auto" aria-label={t("common.more")}>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => setSelectedContactIds(new Set(contacts.map((c) => c.id)))}
                  disabled={contacts.length === 0}
                >
                  {t("common.selectAll")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleExportCsv} disabled={selectedContactIds.size === 0}>
                  <Download className="h-4 w-4 mr-2" />
                  {t("contacts.export")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setDeleteDialogOpen(true)}
                  disabled={selectedContactIds.size === 0 || deleteContactsMutation.isPending}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t("contacts.delete")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      ) : null}

      {isMobile ? (
        <Sheet
          open={addContactOpen}
          onOpenChange={(open) => {
            setAddContactOpen(open);
            if (!open) resetAddContactForm();
          }}
        >
          <SheetContent
            side="bottom"
            className={cn(
              bottomSheetDockClassName(),
              "flex max-h-[min(90dvh,720px)] flex-col overflow-hidden gap-0",
            )}
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <div className="mx-auto mb-2 h-1 w-10 shrink-0 rounded-full bg-muted" aria-hidden />
            <SheetHeader className="max-[767px]:space-y-0 space-y-0 px-0 pb-2 pt-0 text-left">
              <SheetTitle className="text-base font-semibold leading-snug">
                {t("contacts.addContactTitle")}
              </SheetTitle>
            </SheetHeader>
            <div className="min-h-0 flex-1 overflow-y-auto px-0">{addContactFields}</div>
            <div className="flex shrink-0 flex-col gap-2 border-t border-border pt-3 sm:flex-row sm:justify-end">
              <Button variant="outline" className="w-full sm:w-auto" type="button" onClick={closeAddContact}>
                {t("common.cancel")}
              </Button>
              <Button
                className="w-full sm:w-auto"
                type="button"
                disabled={!acName.trim() || createContactMutation.isPending}
                onClick={() =>
                  createContactMutation.mutate({
                    name: acName.trim(),
                    email: acEmail.trim() || undefined,
                    phone: acPhone.trim() || undefined,
                    city: acCity.trim() || undefined,
                    street: acStreet.trim() || undefined,
                    isCompany: acIsCompany,
                    ico: acIsCompany && acIco.trim() ? acIco.trim() : undefined,
                  })
                }
              >
                {createContactMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2 inline" />
                ) : null}
                {t("contacts.addContactSubmit")}
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      ) : (
        <Dialog
          open={addContactOpen}
          onOpenChange={(open) => {
            setAddContactOpen(open);
            if (!open) resetAddContactForm();
          }}
        >
          <DialogContent className="sm:max-w-md" onOpenAutoFocus={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle>{t("contacts.addContactTitle")}</DialogTitle>
            </DialogHeader>
            {addContactFields}
            <DialogFooter>
              <Button variant="outline" type="button" onClick={closeAddContact}>
                {t("common.cancel")}
              </Button>
              <Button
                type="button"
                disabled={!acName.trim() || createContactMutation.isPending}
                onClick={() =>
                  createContactMutation.mutate({
                    name: acName.trim(),
                    email: acEmail.trim() || undefined,
                    phone: acPhone.trim() || undefined,
                    city: acCity.trim() || undefined,
                    street: acStreet.trim() || undefined,
                    isCompany: acIsCompany,
                    ico: acIsCompany && acIco.trim() ? acIco.trim() : undefined,
                  })
                }
              >
                {createContactMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2 inline" />
                ) : null}
                {t("contacts.addContactSubmit")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("contacts.deleteConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("contacts.deleteConfirmDescription", { count: selectedContactIds.size })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteContactsMutation.isPending}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteContactsMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(e) => {
                e.preventDefault();
                deleteContactsMutation.mutate([...selectedContactIds]);
              }}
            >
              {deleteContactsMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin inline" />
                  {t("common.loading")}
                </>
              ) : (
                t("contacts.delete")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ContactsHubShell>
  );
}

function ContactMobileCard({
  contact,
  selectionMode,
  selected,
  onToggleSelect,
  onEnterSelectionMode,
}: {
  contact: Contact;
  selectionMode: boolean;
  selected: boolean;
  onToggleSelect: (contactId: string, checked: boolean) => void;
  onEnterSelectionMode: (contactId: string) => void;
}) {
  const { t } = useTranslation();
  const [sfLoading, setSfLoading] = useState(false);
  const phone = contact.phone ?? null;
  const email = contact.email ?? null;
  const longPress = useLongPress({
    enabled: !selectionMode,
    onLongPress: () => onEnterSelectionMode(contact.id),
  });

  const handleSuperfaktura = async () => {
    setSfLoading(true);
    try {
      const r = await fetch(apiUrl(`/api/crm/contacts/${contact.id}/superfaktura-client-url`), {
        credentials: "include",
      });
      const data = await r.json().catch(() => ({}));
      if (data?.url) {
        window.open(data.url, "_blank", "noopener,noreferrer");
      } else {
        notify.info(t("contacts.noSuperfakturaClient"));
      }
    } finally {
      setSfLoading(false);
    }
  };

  return (
    <div
      className={cn(
        "rounded-md border bg-card p-3 space-y-3",
        selectionItemOutlineClasses(selectionMode, selected),
      )}
      {...longPress}
      onClick={() => {
        if (selectionMode) onToggleSelect(contact.id, !selected);
      }}
    >
      <div>
        <div className="font-medium text-foreground">{contact.name || "—"}</div>
        <div className="text-sm text-muted-foreground">{email || "—"}</div>
      </div>
      <div className="text-sm text-muted-foreground">{phone || "—"}</div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={() => phone?.trim() && (window.location.href = `tel:${phone.trim()}`)}
          disabled={!phone?.trim()}
        >
          <Phone className="h-4 w-4 mr-1" />
          {t("contacts.call")}
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={() => email?.trim() && (window.location.href = `mailto:${email.trim()}`)}
          disabled={!email?.trim()}
        >
          <Mail className="h-4 w-4 mr-1" />
          {t("contacts.email")}
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={handleSuperfaktura}
          disabled={sfLoading}
          title={t("contacts.superfaktura")}
        >
          {sfLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

function ContactRow({
  contact,
  selectionMode,
  selected,
  onToggleSelect,
}: {
  contact: Contact;
  selectionMode: boolean;
  selected: boolean;
  onToggleSelect: (contactId: string, checked: boolean) => void;
}) {
  const { t } = useTranslation();
  const [sfLoading, setSfLoading] = useState(false);

  const phone = contact.phone ?? null;
  const email = contact.email ?? null;
  const city = contact.city ?? null;
  const street = contact.street ?? null;

  const handleCall = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!phone?.trim()) return;
    window.location.href = `tel:${phone.trim()}`;
  };

  const handleEmail = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!email?.trim()) return;
    window.location.href = `mailto:${email.trim()}`;
  };

  const handleSuperfaktura = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setSfLoading(true);
    try {
      const r = await fetch(apiUrl(`/api/crm/contacts/${contact.id}/superfaktura-client-url`), {
        credentials: "include",
      });
      const data = await r.json().catch(() => ({}));
      if (data?.url) {
        window.open(data.url, "_blank", "noopener,noreferrer");
      } else {
        notify.info(t("contacts.noSuperfakturaClient"));
      }
    } finally {
      setSfLoading(false);
    }
  };

  const handleRowClick = () => {
    if (selectionMode) onToggleSelect(contact.id, !selected);
  };

  return (
    <TableRow
      className={cn(
        "align-top border-b border-border last:border-b-0 transition-colors",
        selectionMode && "cursor-pointer hover:bg-muted/30",
        selectionItemOutlineClasses(selectionMode, selected, { denseSelectedBg: true }),
      )}
      onClick={selectionMode ? handleRowClick : undefined}
    >
      {selectionMode && (
        <TableCell className="w-10 py-3 align-top" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={selected}
            onCheckedChange={(checked) => onToggleSelect(contact.id, !!checked)}
          />
        </TableCell>
      )}
      <TableCell className="font-medium py-3 align-top">{contact.name || "—"}</TableCell>
      <TableCell className="text-muted-foreground truncate max-w-[200px] py-3 align-top" title={email ?? undefined}>
        {email || "—"}
      </TableCell>
      <TableCell className="text-muted-foreground py-3 align-top">{phone || "—"}</TableCell>
      <TableCell className="text-muted-foreground py-3 align-top">{city || "—"}</TableCell>
      <TableCell className="text-muted-foreground truncate max-w-[160px] py-3 align-top" title={street ?? undefined}>
        {street || "—"}
      </TableCell>
      <TableCell className="text-right py-3 align-top">
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={handleCall}
            disabled={!phone?.trim()}
            title={t("contacts.call")}
          >
            <Phone className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={handleEmail}
            disabled={!email?.trim()}
            title={t("contacts.email")}
          >
            <Mail className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={handleSuperfaktura}
            disabled={sfLoading}
            title={t("contacts.superfaktura")}
          >
            {sfLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ExternalLink className="h-4 w-4" />
            )}
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
